import { useEffect, useState } from "react";
import axios from "axios";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
        setFilteredProducts(response.data);
      } catch (e) {
        console.error("Error fetching products:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (categoryFilter === "all") {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(p => p.category === categoryFilter));
    }
  }, [categoryFilter, products]);

  const categories = ["all", ...new Set(products.map(p => p.category))];

  return (
    <div className="min-h-screen py-20" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl text-brand-dark mb-4">
            Care Package <span className="text-brand-coral">Shop</span>
          </h1>
          <p className="font-body text-lg text-brand-muted max-w-2xl mx-auto">
            Browse our selection of curated care packages designed to bring comfort and joy to your college student. Click "Add to Cart" to start your order!
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-between items-center mb-8 gap-4">
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-brand-muted">Filter by:</span>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40 bg-white" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat === "all" ? "All Categories" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="font-body text-sm text-brand-muted">
            {filteredProducts.length} package{filteredProducts.length !== 1 ? 's' : ''} available
          </p>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl h-96 animate-pulse" />
            ))}
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
            {filteredProducts.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product} 
                showAddToCart={true}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-products">
            <Package size={48} className="mx-auto text-brand-muted/40 mb-4" />
            <p className="font-body text-brand-muted">No packages found in this category.</p>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-16 bg-brand-secondary/20 rounded-2xl p-8 md:p-12 text-center" data-testid="info-box">
          <h3 className="font-heading text-2xl text-brand-dark mb-4">
            All Products Are Placeholders
          </h3>
          <p className="font-body text-brand-muted max-w-2xl mx-auto">
            The products shown are placeholder items. Actual care package details, descriptions, and pricing will be added soon. Each package will be thoughtfully curated for college students.
          </p>
        </div>

        {/* Stripe Ready Notice */}
        <div className="mt-8 bg-white rounded-xl p-6 border border-brand-secondary/30 text-center" data-testid="stripe-notice">
          <p className="font-body text-sm text-brand-muted">
            <span className="font-medium text-brand-primary">Checkout Integration Ready</span> — This shop is prepared for payment processing integration (Stripe).
          </p>
        </div>
      </div>
    </div>
  );
};

export default Shop;
